import { useApolloClient } from "@apollo/client";
import { LabeledValue } from "@msys/ui";
import { Link as MuiLink, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { omit } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import {
  namedOperations,
  PhoneInput,
} from "../../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import {
  PHONE_REG_EXP,
  prepareUrl,
  SITE_REG_EXP,
} from "../../../commons/form-fields/helpers.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { LabeledPhoneValue } from "../../phones/LabeledPhoneValue.js";
import { PhonesField } from "../../phones/PhonesField.js";
import { defaultPhoneType } from "../../phones/usePhoneTypes.js";
import { SectionCard } from "../sections/SectionCard.js";
import {
  OrganisationDetailsDataBox_ViewerOrganisationFragment,
  useOrganisationDetailsDataBox_ModifyOrganisationMutation,
} from "./OrganisationDetailsDataBox.generated.js";

interface Props {
  organisation: OrganisationDetailsDataBox_ViewerOrganisationFragment;
  isViewerOrganisation: boolean;
  debug: boolean;
}

interface CompanyDataFormValues {
  title: string;
  email: string;
  website: string;
  phones: PhoneInput[];
}

function CompanyDataForm({
  organisation,
  isViewerOrganisation,
}: {
  organisation: OrganisationDetailsDataBox_ViewerOrganisationFragment;
  isViewerOrganisation: boolean;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["OrganisationProfile", "Global"]);
  const client = useApolloClient();

  const [modifyOrganisation] =
    useOrganisationDetailsDataBox_ModifyOrganisationMutation({
      client,
      refetchQueries: [namedOperations.Query.OrganisationDetails],
    });

  const initialValues: CompanyDataFormValues = React.useMemo(
    () => ({
      title: organisation.title,
      email: organisation.email,
      website: organisation.website,
      phones:
        organisation.phones?.length > 0
          ? organisation.phones
          : [defaultPhoneType],
    }),
    [
      organisation.title,
      organisation.email,
      organisation.website,
      organisation.phones,
    ]
  );

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .label(
        isViewerOrganisation
          ? t("Name", { ns: "OrganisationProfile" })
          : t("Account Name", { ns: "OrganisationProfile" })
      )
      .required(),
    email: Yup.string()
      .label(t("Email", { ns: "OrganisationProfile" }))
      .email()
      .trim(),
    website: Yup.string()
      .label(t("Website", { ns: "OrganisationProfile" }))
      .matches(
        SITE_REG_EXP,
        t("Website is not valid", { ns: "OrganisationProfile" })
      ),
    phones: Yup.array().of(
      Yup.object().shape({
        id: Yup.string(),
        main: Yup.boolean(),
        type: Yup.string(),
        number: Yup.string().matches(
          PHONE_REG_EXP,
          t("Phone number is not valid", { ns: "OrganisationProfile" })
        ),
      })
    ),
  });

  const handleSubmit = async (values: CompanyDataFormValues) => {
    try {
      const input = {
        ...(isViewerOrganisation ? {} : { organisationId: organisation.id }),
        title: values.title,
        website: values.website,
        email: values.email,
        phones: values.phones
          .filter(p => p.number.trim())
          .map(p => omit(p, "__typename", "id")),
      };

      await modifyOrganisation({
        variables: { input },
      });
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    }
  };

  return (
    <Formik<CompanyDataFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {formikProps => (
        <Form>
          <Stack spacing={1}>
            <TextField
              name="title"
              label={
                isViewerOrganisation
                  ? t("Name", { ns: "OrganisationProfile" })
                  : t("Account Name", { ns: "OrganisationProfile" })
              }
              required
              disabled={false}
            />
            <TextField
              disabled={false}
              name="website"
              label={t("Website", { ns: "OrganisationProfile" })}
            />
            <TextField
              disabled={false}
              name="email"
              label={t("Email", { ns: "OrganisationProfile" })}
              type="email"
            />
            <PhonesField
              name="phones"
              buttonLabel={t("Add phone number", {
                ns: "OrganisationProfile",
              })}
              value={formikProps.values.phones}
              disabled={false}
            />
            <AutoSave />
          </Stack>
        </Form>
      )}
    </Formik>
  );
}

function CompanyDataView({ organisation, isViewerOrganisation, debug }: Props) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  return (
    <>
      <LabeledValue
        label={
          isViewerOrganisation
            ? t("Name", { ns: "OrganisationProfile" })
            : t("Account Name", { ns: "OrganisationProfile" })
        }
      >
        {organisation.title || t("Not set", { ns: "Global" })}
      </LabeledValue>
      {debug && <Typography variant="body2">{organisation.id}</Typography>}
      <LabeledValue label={t("Website", { ns: "OrganisationProfile" })}>
        {organisation.website ? (
          <MuiLink
            href={prepareUrl(organisation.website)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {organisation.website}
          </MuiLink>
        ) : (
          t("Not set", { ns: "Global" })
        )}
      </LabeledValue>
      <LabeledValue label={t("Email", { ns: "OrganisationProfile" })}>
        {organisation.email ? (
          <MuiLink href={`mailto:${organisation.email}`}>
            {organisation.email}
          </MuiLink>
        ) : (
          t("Not set", { ns: "Global" })
        )}
      </LabeledValue>
      <LabeledPhoneValue
        label={t("Phone", { ns: "Global" })}
        phones={organisation.phones}
      />
    </>
  );
}

export function OrganisationDetailsDataBox({
  organisation,
  isViewerOrganisation,
  debug,
}: Props) {
  const { t } = useTranslate(["OrganisationProfile"]);
  const viewer = useUserData().currentUser!;

  return (
    <SectionCard title={t("Organisation", { ns: "OrganisationProfile" })}>
      <RestrictedByOrganisationPermissionWithDebug
        permission="organisation_profile_update"
        otherwise={
          <CompanyDataView
            organisation={organisation}
            isViewerOrganisation={isViewerOrganisation}
            debug={debug}
          />
        }
      >
        {viewer.organisation.id === organisation.id &&
        !!viewer.roles.find(r => r.internalName === "ORG_ADMIN") ? (
          <CompanyDataForm
            organisation={organisation}
            isViewerOrganisation={isViewerOrganisation}
          />
        ) : (
          <CompanyDataView
            organisation={organisation}
            isViewerOrganisation={isViewerOrganisation}
            debug={debug}
          />
        )}
      </RestrictedByOrganisationPermissionWithDebug>
    </SectionCard>
  );
}
